import React, { useState } from 'react';
import { Text } from '@chakra-ui/react';

import {
  UploadActions,
  UploadButtonSection,
  UploadExample,
  UploadComplete as UploadCompleteSection,
  ModalColumns,
  AcknowledgeBox,
  RequirementsRemedy,
  RadioOptionsWithConfirm,
  IQuestion
} from 'shared-components';

import placeholderExample from '../../../../../assets/images/sample-deposit-bank-transfer.jpg';
import { IDocument } from '../../../../../types';

interface IProps {
  sectionTitle?: string;
  fileNames: string[];
  document: IDocument;
  hasCopyOfDepositReceipt?: boolean;
  onSaveMetaDataForNoDoc: (metadata?: any) => void;
  onContinue: () => void;
  onUpload: (metadata?: any) => void;
  onClose: () => void;
  docKey: string;
  infoOnly: boolean;
  docStatus: string;
  onAcknowledgeToggle: (status: string) => void;
}

const DepositModal: React.FC<IProps> = (props: IProps) => {
  const { infoOnly, docStatus, docKey } = props;

  const [hasCopyOfDepositReceipt, setHasCopyOfDepositReceipt] = useState<boolean | undefined>(
    props.hasCopyOfDepositReceipt
  );

  const onContinue = () => {
    props.onContinue();
    props.onClose();
  };

  const handleOnClose = () => {
    if (docStatus === 'INFO_ONLY') {
      props.onAcknowledgeToggle('NOT_ACKNOWLEDGED');
    }
    props.onClose();
  };

  const toggleAcknowledge = (key: string, value: boolean) => {
    // prevent call to API if previous call not yet complete
    if ((value === true && docStatus !== 'ACKNOWLEDGED') || (value === false && docStatus === 'ACKNOWLEDGED')) {
      props.onAcknowledgeToggle(value === true ? 'ACKNOWLEDGED' : 'NOT_ACKNOWLEDGED');
    }
  };

  return (
    <ModalColumns
      id={'depositProof'}
      title={'Prove your deposit payment'}
      onClose={handleOnClose}
      supportingColumn={<UploadExample altText="deposit bank transfer example" url={placeholderExample} />}
    >
      {infoOnly ? (
        <>
          <Text>
            During your visa application you may be required to provide proof of your deposit. Please ensure you retain
            any transfer receipts you received from the bank when you made your deposit transfer to the University.
          </Text>

          <AcknowledgeBox
            id={docKey}
            blurb="Please confirm you have read and understand the above."
            isTicked={docStatus === 'ACKNOWLEDGED'}
            onChange={toggleAcknowledge}
          />

          <UploadActions
            label="Return to document upload"
            complete={docStatus === 'ACKNOWLEDGED'}
            onContinue={handleOnClose}
          />
        </>
      ) : (
        <>
          {props.docStatus !== 'AWAITING' && hasCopyOfDepositReceipt !== false ? (
            <UploadCompleteSection
              fileNames={props.fileNames}
              onUpload={() => props.onUpload({ hasCopyOfDepositReceipt })}
              onContinue={onContinue}
            />
          ) : (
            <>
              <Text>
                We require proof of your deposit. Please scan and/or upload any transfer receipts you received from the
                bank when you made the deposit transfer to the University.
              </Text>

              <RadioOptionsWithConfirm
                label={'Are you able to get a copy of your deposit receipt?'}
                questions={[
                  {
                    char: 'Y',
                    label: 'Yes',
                    selected: hasCopyOfDepositReceipt === true
                  },
                  {
                    char: 'N',
                    label: 'No',
                    selected: hasCopyOfDepositReceipt === false
                  }
                ]}
                isInline={true}
                onClick={(question: IQuestion) => {
                  const isYes = question.label === 'Yes';
                  setHasCopyOfDepositReceipt(isYes);
                  props.onSaveMetaDataForNoDoc({
                    hasCopyOfDepositReceipt: isYes
                  });
                }}
              />

              {hasCopyOfDepositReceipt ? (
                <UploadButtonSection onUpload={() => props.onUpload({ hasCopyOfDepositReceipt })}>
                  Awaiting upload of <b>Deposit Proof</b>
                </UploadButtonSection>
              ) : null}

              {hasCopyOfDepositReceipt === false ? (
                <RequirementsRemedy isSatisfied>
                  This item will be marked as Done. You may be contacted for more information.
                </RequirementsRemedy>
              ) : null}

              <UploadActions
                label="Return to document upload"
                complete={hasCopyOfDepositReceipt === false}
                onContinue={onContinue}
              />
            </>
          )}
        </>
      )}
    </ModalColumns>
  );
};

export default DepositModal;
